import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LockIcon, PlugZapIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { bundleName, formatMoney, getBundle, priceLine } from '../../utils/pricing';
import { buildCartPermalink } from '../../utils/shopify';
import { track } from '../../utils/analytics';
import { buttonClasses } from '../../utils/buttonStyles';
export function Checkout() {
  const {
    lines,
    totals
  } = useCart();
  const {
    lang
  } = useLanguage();
  const m = (n: number) => formatMoney(n, lang);
  const permalink = buildCartPermalink(lines);
  useEffect(() => {
    if (!lines.length) return;
    const contents = lines.map(l => {
      const p = priceLine(l);
      return l.kind === 'system' ? {
        id: getBundle(l.bundleId).variantId,
        quantity: 1,
        item_price: p.total
      } : {
        id: getBundle('sys1').variantId,
        quantity: l.qty,
        item_price: p.total / l.qty
      };
    });
    track('begin_checkout', {
      value: totals.total,
      currency: 'USD',
      content_ids: contents.map(c => c.id),
      content_type: 'product',
      contents,
      num_items: totals.lights
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!lines.length) {
    return <div className="mx-auto max-w-site px-5 py-20 md:px-8" data-id="element-1375" data-mp="204ocaxut8u2y">
        <h1 className="font-display text-5xl font-semibold uppercase" data-id="element-1376" data-mp="2dww40b78luus">Checkout</h1>
        <p className="mt-4 text-titanium" data-id="element-1377" data-mp="29wc635euan7l">Your cart is empty.</p>
        <Link to="/shop" className={`${buttonClasses('primary')} mt-6`} data-id="element-1378" data-mp="29y53rv7h5x3g">
          Build your system
        </Link>
      </div>;
  }
  return <div className="mx-auto grid max-w-site gap-12 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_420px]" data-id="element-1379" data-mp="2iw8mykuzmhs3">
      <div data-id="element-1380" data-mp="th0xbg7oyzmd">
        <h1 className="font-display text-5xl font-semibold uppercase md:text-6xl" data-id="element-1381" data-mp="tilpx0ag48hl">Secure Checkout</h1>
        <p className="mt-4 max-w-xl text-titanium" data-id="element-1382" data-mp="ok0z1rh3315t">
          Complete your order on our 256-bit SSL encrypted checkout. Your payment details are protected and never stored by MarineForceLED.
        </p>

        {permalink.url ? <a href={permalink.url} onClick={() => track('checkout_handoff', {
        value: totals.total
      })} className={`${buttonClasses('primary', 'lg')} mt-10`} data-id="element-1383" data-mp="2tm66evi7nwvc">
            <LockIcon className="h-4 w-4" data-id="element-1384" data-mp="32idqs8xphjkc" /> Continue to secure checkout
          </a> : <div className="mt-10 rounded-2xl border border-ice/25 bg-ice/[0.05] p-6 md:p-8" data-id="element-1385" data-mp="37i0dd8zfcc5t">
            <div className="flex items-center gap-3" data-id="element-1386" data-mp="3cgtkmjo4b6xj">
              <PlugZapIcon className="h-5 w-5 text-ice" aria-hidden data-id="element-1387" data-mp="3hapdj7piy3lu" />
              <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1388" data-mp="3lazs38rdu2cb">Secure checkout opening soon</h2>
            </div>
            <p className="mt-3 text-titanium" data-id="element-1389" data-mp="3qbdrk2ex43yq">
              Online checkout is launching shortly. Your cart is saved on this device — contact our team if you'd like help
              with your order.
            </p>
            <button type="button" disabled className={`${buttonClasses('primary', 'lg')} mt-6`} data-id="element-1390" data-mp="99janbuf76h0">
              <LockIcon className="h-4 w-4" data-id="element-1391" data-mp="47aek6m93oao" /> Continue to secure checkout
            </button>
          </div>}

        <Link to="/cart" className="mt-6 inline-block text-sm text-titanium hover:text-white" data-id="element-1392" data-mp="3vh942nhuml9v">
          ← Return to cart
        </Link>
      </div>

      <aside aria-label="Order summary" className="rounded-2xl border mf-hairline bg-navy p-6 md:p-8 lg:self-start" data-id="element-1393" data-mp="rypl6bl28rrq">
        <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1394" data-mp="n0eeuatz4ze9">Your order</h2>
        <ul className="mt-5 divide-y divide-white/10" data-id="element-1395" data-mp="i7uwz1o97i3n">
          {lines.map(l => {
          const p = priceLine(l);
          const name = l.kind === 'system' ? bundleName(getBundle(l.bundleId), lang) : 'M1 PRO — Build Your Own';
          return <li key={l.id} className="flex justify-between gap-4 py-4 text-sm" data-id="element-1396" data-mp="1aqs3drdcog43">
                <span data-id="element-1397" data-mp="15w4ehrefpjoz">
                  <span className="block text-white" data-id="element-1398" data-mp="qg7jvdnggd9q">{name}</span>
                  <span className="text-titanium" data-id="element-1399" data-mp="qewej9ed2xgi">
                    {p.lights} lights{l.kind === 'system' && l.extras ? ` (incl. ${l.extras} extra)` : ''}
                  </span>
                </span>
                <span className="text-white" data-id="element-1400" data-mp="hhm2hltpz4aj">{m(p.total)}</span>
              </li>;
        })}
        </ul>
        <div className="mt-4 flex items-baseline justify-between border-t mf-hairline pt-4" data-id="element-1401" data-mp="196p752piqgnp">
          <span className="text-titanium" data-id="element-1402" data-mp="1991obdngcmzp">Subtotal</span>
          <span className="font-display text-3xl font-semibold" data-id="element-1403" data-mp="1e4gz1wq5y2rw">{m(totals.total)}</span>
        </div>
        <p className="mt-2 text-xs text-titanium/70" data-id="element-1404" data-mp="10dlxz6lwopyv">Free U.S. shipping. Taxes calculated at checkout.</p>
        <p className="mt-5 border-t mf-hairline pt-4 text-sm text-titanium" data-id="element-1405" data-mp="14efjxufsfup4">
          Professional-grade lighting, backed by professional-grade support — the MarineForce Promise.
        </p>
      </aside>
    </div>;
}